import React, { useEffect, useId, useRef, useState } from 'react';
import { useErrorReport } from '../../../contexts/ErrorReportContext';
import {
  makeExploreMediaThumb,
  optimizeExploreMedia,
} from '../../../services/exploreMediaOptimize';
import {
  addMemoryContribution,
  inviteToMemory,
  isMemoryOpen,
  memorySharePath,
  nextMemoryOpenLabel,
  subscribeMemoryContributions,
} from '../../../services/memories';
import { getFriendsProfiles } from '../../../services/userProfile';
import './GameSheets.css';

/**
 * Memory detail: full collage when open; locked teaser otherwise.
 * Members can contribute anytime; media only renders while open.
 */
const GameMemoryDetailSheet = ({
  memory,
  uid,
  friendIds = [],
  onClose,
  onUpdated,
}) => {
  const { reportError } = useErrorReport();
  const [contributions, setContributions] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [friends, setFriends] = useState([]);
  const [selectedInviteIds, setSelectedInviteIds] = useState(() => new Set());
  const [contributeType, setContributeType] = useState('photo');
  const [contributeText, setContributeText] = useState('');
  const [contributeFile, setContributeFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [copied, setCopied] = useState(false);
  const closeRef = useRef(null);
  const baseId = useId();
  const fileId = `${baseId}-contrib-file`;

  const open = Boolean(memory);
  const memoryOpen = isMemoryOpen(memory);
  const isMember = Boolean(uid && memory?.memberUids?.includes(uid));
  const memberCount = memory?.memberUids?.length || 0;

  useEffect(() => {
    if (!open || !memory?.id || !isMember) {
      setContributions([]);
      return undefined;
    }
    return subscribeMemoryContributions(
      memory.id,
      setContributions,
      (err) => setLoadError(err?.message || 'Could not load contributions.')
    );
  }, [open, memory?.id, isMember]);

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  useEffect(() => {
    if (!inviteOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await getFriendsProfiles(friendIds);
        if (!cancelled) {
          setFriends(
            list.filter((f) => f?.id && !(memory?.memberUids || []).includes(f.id))
          );
        }
      } catch {
        if (!cancelled) setFriends([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteOpen, friendIds, memory?.memberUids]);

  if (!open) return null;

  const title = memory.title?.trim() || 'Memory';
  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${memorySharePath(memory.id)}`
      : memorySharePath(memory.id);

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setStatus(shareUrl);
    }
  };

  const toggleInvite = (id) => {
    setSelectedInviteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sendInvites = async () => {
    if (!selectedInviteIds.size) return;
    setBusy(true);
    try {
      const updated = await inviteToMemory(memory.id, [...selectedInviteIds], {
        fromUid: uid,
        memoryTitle: memory.title || '',
      });
      onUpdated?.(updated);
      setInviteOpen(false);
      setSelectedInviteIds(new Set());
      setStatus('Friends invited.');
    } catch (err) {
      reportError(err, {
        pageLabel: 'Memory',
        source: 'memory-invite',
        userMessage: err?.message || 'Could not invite.',
      });
    } finally {
      setBusy(false);
    }
  };

  const submitContribution = async (e) => {
    e.preventDefault();
    if (busy) return;
    if ((contributeType === 'photo' || contributeType === 'video') && !contributeFile) {
      setStatus(contributeType === 'photo' ? 'Choose a photo.' : 'Choose a video.');
      return;
    }
    if (contributeType === 'journal' && !contributeText.trim()) {
      setStatus('Write a short note.');
      return;
    }
    setBusy(true);
    setStatus('');
    try {
      let uploadFile = contributeFile;
      let thumbFile = null;
      if (contributeType === 'photo' || contributeType === 'video') {
        uploadFile = await optimizeExploreMedia(contributeType, contributeFile);
        thumbFile = await makeExploreMediaThumb(contributeType, uploadFile);
      }
      await addMemoryContribution({
        memoryId: memory.id,
        uid,
        type: contributeType,
        text: contributeText,
        file: uploadFile,
        thumbFile,
      });
      setContributeFile(null);
      setContributeText('');
      setStatus(
        memoryOpen
          ? 'Added to memory.'
          : 'Saved — visible when the memory opens again.'
      );
    } catch (err) {
      reportError(err, {
        pageLabel: 'Memory',
        source: 'memory-contribute',
        userMessage: err?.message || 'Could not add contribution.',
      });
      setStatus(err?.message || 'Could not add.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="game-sheet" role="dialog" aria-modal="true" aria-labelledby={`${baseId}-title`}>
      <button
        type="button"
        className="game-sheet__backdrop"
        aria-label="Close"
        onClick={() => !busy && onClose?.()}
      />
      <div className="game-sheet__panel game-memory-sheet">
        <header className="game-sheet__header">
          <h2 id={`${baseId}-title`} className="game-sheet__title">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="game-sheet__close"
            aria-label="Close"
            onClick={() => onClose?.()}
          >
            ×
          </button>
        </header>

        <p className="game-memory-sheet__meta">
          {memberCount} member{memberCount === 1 ? '' : 's'}
          {memoryOpen ? ' · Open today' : ` · Opens ${nextMemoryOpenLabel(memory)}`}
        </p>

        {!memoryOpen ? (
          <div className="game-memory-sheet__locked">
            <p className="game-memory-sheet__locked-title">Locked</p>
            <p>
              Media stays hidden until the anniversary day. You can still add
              contributions anytime — they appear when it opens.
            </p>
            <p className="game-memory-sheet__locked-count">
              {contributions.length
                ? `${contributions.length} piece${contributions.length === 1 ? '' : 's'} waiting`
                : 'No contributions yet'}
            </p>
          </div>
        ) : (
          <ul className="game-memory-sheet__collage">
            {contributions.map((c) => (
              <li key={c.id} className={`game-memory-sheet__piece game-memory-sheet__piece--${c.type}`}>
                {c.type === 'photo' && c.mediaUrl ? (
                  <img src={c.mediaUrl} alt="" />
                ) : null}
                {c.type === 'video' && c.mediaUrl ? (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video src={c.mediaUrl} controls playsInline loop />
                ) : null}
                {c.type === 'journal' || c.text ? (
                  <p className="game-memory-sheet__piece-text">{c.text || 'Journal'}</p>
                ) : null}
              </li>
            ))}
            {!contributions.length && !loadError ? (
              <li className="game-memory-sheet__empty">No pieces yet — add the first.</li>
            ) : null}
          </ul>
        )}

        {loadError ? (
          <p className="game-memory-sheet__error" role="alert">
            {loadError}
          </p>
        ) : null}

        {isMember ? (
          <>
            <div className="game-memory-sheet__share-row">
              <button type="button" className="game-memory-sheet__file-btn" onClick={copyShare}>
                {copied ? 'Link copied' : 'Copy share link'}
              </button>
              <button
                type="button"
                className="game-memory-sheet__file-btn"
                onClick={() => setInviteOpen((v) => !v)}
              >
                Invite friends
              </button>
            </div>

            {inviteOpen ? (
              <div className="game-memory-sheet__invites">
                {friends.length ? (
                  <ul className="game-memory-sheet__friend-list">
                    {friends.map((f) => (
                      <li key={f.id}>
                        <label className="game-memory-sheet__friend">
                          <input
                            type="checkbox"
                            checked={selectedInviteIds.has(f.id)}
                            onChange={() => toggleInvite(f.id)}
                          />
                          <span>{f.displayName || f.username || 'Friend'}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="game-memory-sheet__hint">No more friends to invite.</p>
                )}
                <button
                  type="button"
                  className="game-memory-sheet__submit"
                  disabled={busy || !selectedInviteIds.size}
                  onClick={sendInvites}
                >
                  Send invites
                </button>
              </div>
            ) : null}

            <form onSubmit={submitContribution} className="game-memory-sheet__contribute">
              <h3 className="game-memory-sheet__contribute-title">Add a piece</h3>
              <div className="game-memory-sheet__types" role="group" aria-label="Contribution type">
                {['photo', 'video', 'journal'].map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`game-memory-sheet__type${contributeType === t ? ' is-active' : ''}`}
                    disabled={busy}
                    onClick={() => {
                      setContributeType(t);
                      if (t === 'journal') setContributeFile(null);
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {contributeType === 'journal' ? (
                <textarea
                  value={contributeText}
                  rows={3}
                  maxLength={500}
                  onChange={(e) => setContributeText(e.target.value)}
                  placeholder="A short note…"
                  disabled={busy}
                />
              ) : (
                <>
                  <label className="game-memory-sheet__file-btn" htmlFor={fileId}>
                    {contributeFile ? contributeFile.name : `Choose ${contributeType}`}
                  </label>
                  <input
                    id={fileId}
                    type="file"
                    accept={
                      contributeType === 'video'
                        ? 'video/mp4,video/webm'
                        : 'image/jpeg,image/png,image/webp'
                    }
                    hidden
                    onChange={(e) => {
                      setContributeFile(e.target.files?.[0] || null);
                      e.target.value = '';
                    }}
                  />
                  <input
                    type="text"
                    value={contributeText}
                    maxLength={200}
                    placeholder="Caption (optional)"
                    onChange={(e) => setContributeText(e.target.value)}
                    disabled={busy}
                  />
                </>
              )}
              <button type="submit" className="game-memory-sheet__submit" disabled={busy}>
                {busy ? 'Saving…' : 'Add'}
              </button>
            </form>
          </>
        ) : null}

        {status ? (
          <p className="game-memory-sheet__hint" role="status">
            {status}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default GameMemoryDetailSheet;
